import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api/client';
import ImageWithPreview from './ImageWithPreview';
import { productImageUrl } from './sets/ProductDetailsField';
import './BulkEditPopup.css';

const MARKETPLACES = [
    { key: 'shopify', label: 'Shopify' },
    { key: 'etsy', label: 'Etsy' },
    { key: 'walmart', label: 'Walmart' },
    { key: 'amazon', label: 'Amazon' },
];
const ALL_MARKETPLACES = MARKETPLACES.map(m => m.key).join(',');

// ponytail: без виртуализации — грузим до 1000 строк, как DataTable; добавить react-window, если таблицы вырастут
const BulkEditPopup = ({ isOpen, onClose, tableName, endpoint, fields, keyField, onSaved }) => {
    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});           // { rowKey: { field: value } }
    const [options, setOptions] = useState({});       // { fieldName: [{value,label}] }
    const [sort, setSort] = useState({ key: null, dir: null });
    const [hiddenCols, setHiddenCols] = useState({});
    const [brandFilter, setBrandFilter] = useState('');
    const [focused, setFocused] = useState(null);     // { rowKey, name }
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [imgVer, setImgVer] = useState({});         // { sku: n } — cache-buster после загрузки фото
    const fileRef = useRef(null);
    const uploadSkuRef = useRef(null);

    const editableFields = useMemo(
        () => (fields || []).filter(f => f.name !== keyField),
        [fields, keyField]
    );

    useEffect(() => {
        if (!isOpen) return;
        setEdits({});
        setSort({ key: null, dir: null });
        setBrandFilter('');
        setError('');
        api.post('/api/bulk-edit-opened', { table: tableName }).catch(() => {});
        // Габариты/UPC/вес живут в общей таблице product_details — подмешиваем их к строкам
        const detailNames = editableFields.filter(f => f.details).map(f => f.name);
        Promise.all([
            api.get(endpoint, { params: { page: 1, limit: 1000 } }),
            detailNames.length ? api.get('/api/product-details') : Promise.resolve(null),
        ])
            .then(([res, detRes]) => {
                let items = res.data.items || res.data.data || res.data || [];
                if (detRes) {
                    const bySku = {};
                    (detRes.data || []).forEach(d => { bySku[d.product_sku] = d; });
                    items = items.map(r => {
                        const d = bySku[r[keyField]] || {};
                        const extra = {};
                        detailNames.forEach(n => { extra[n] = d[n] !== undefined ? d[n] : null; });
                        return { ...r, ...extra };
                    });
                }
                setRows(items);
            })
            .catch(err => {
                console.error('Bulk edit: error fetching rows', err);
                setError('Failed to load data');
            });
        editableFields.filter(f => f.type === 'select').forEach(f => {
            if (f.options) {
                setOptions(prev => ({ ...prev, [f.name]: f.options }));
                return;
            }
            api.get(`/api/options/${f.tableName}`)
                .then(res => {
                    const items = res.data.items || [];
                    setOptions(prev => ({
                        ...prev,
                        [f.name]: items.map(item => ({
                            value: String(item[f.updateField]),
                            label: item[f.displayField] ?? (f.displayFallback && item[f.displayFallback]) ?? item[f.updateField] ?? ''
                        }))
                    }));
                })
                .catch(err => console.error('Bulk edit: error fetching options', err));
        });
    }, [isOpen, endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

    const getValue = (row, name) => {
        const rowEdits = edits[row[keyField]];
        return rowEdits && rowEdits[name] !== undefined ? rowEdits[name] : row[name];
    };

    const isChanged = (row, name) => {
        const rowEdits = edits[row[keyField]];
        return !!rowEdits && rowEdits[name] !== undefined;
    };

    const setCell = (row, name, value) => {
        const rowKey = row[keyField];
        setEdits(prev => {
            const rowEdits = { ...(prev[rowKey] || {}) };
            if (String(value ?? '') === String(row[name] ?? '')) delete rowEdits[name];
            else rowEdits[name] = value;
            const next = { ...prev };
            if (Object.keys(rowEdits).length) next[rowKey] = rowEdits;
            else delete next[rowKey];
            return next;
        });
    };

    const labelFor = (name, value) => {
        const opt = (options[name] || []).find(o => o.value === String(value ?? ''));
        return opt ? opt.label : '';
    };

    const hasBrand = rows.length > 0 && rows[0].brandName !== undefined && tableName !== 'brands';
    const brandNames = useMemo(
        () => [...new Set(rows.map(r => r.brandName).filter(Boolean))].sort(),
        [rows]
    );

    const displayedRows = useMemo(() => {
        let result = brandFilter ? rows.filter(r => r.brandName === brandFilter) : rows;
        if (sort.key && sort.dir) {
            const field = editableFields.find(f => f.name === sort.key);
            const dir = sort.dir === 'asc' ? 1 : -1;
            const val = (row) => {
                let v = field && field.type === 'select' ? labelFor(sort.key, getValue(row, sort.key)) : getValue(row, sort.key);
                if (v === null || v === undefined || v === '') return null;
                const num = parseFloat(v);
                return !isNaN(num) && isFinite(v) ? num : String(v).toLowerCase();
            };
            result = [...result].sort((a, b) => {
                const av = val(a), bv = val(b);
                if (av === null && bv === null) return 0;
                if (av === null) return 1;
                if (bv === null) return -1;
                if (av < bv) return -dir;
                if (av > bv) return dir;
                return 0;
            });
        }
        return result;
    }, [rows, brandFilter, sort, edits, options]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSort = (name) => {
        setSort(prev => prev.key !== name ? { key: name, dir: 'asc' }
            : prev.dir === 'asc' ? { key: name, dir: 'desc' }
            : { key: null, dir: null });
    };

    const fillDown = (fromIndex, name) => {
        const value = getValue(displayedRows[fromIndex], name);
        setEdits(prev => {
            const next = { ...prev };
            displayedRows.slice(fromIndex + 1).forEach(row => {
                const rowKey = row[keyField];
                const rowEdits = { ...(next[rowKey] || {}) };
                if (String(value ?? '') === String(row[name] ?? '')) delete rowEdits[name];
                else rowEdits[name] = value;
                if (Object.keys(rowEdits).length) next[rowKey] = rowEdits;
                else delete next[rowKey];
            });
            return next;
        });
    };

    const handleImageUpload = (sku, kind) => {
        uploadSkuRef.current = { sku, kind: kind || '' };
        if (fileRef.current) fileRef.current.click();
    };

    const handleImageFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        const target = uploadSkuRef.current;
        if (!file || !target) return;
        const fd = new FormData();
        fd.append('image', file);
        try {
            await api.post(`/api/product-details/${encodeURIComponent(target.sku)}/image${target.kind ? '/' + target.kind : ''}`, fd);
            setImgVer(prev => ({ ...prev, [`${target.sku}|${target.kind}`]: (prev[`${target.sku}|${target.kind}`] || 0) + 1 }));
        } catch (err) {
            setError(err.response?.data?.error || `Image upload failed for ${target.sku}`);
        }
    };

    const toggleMarketplace = (row, name, key) => {
        const current = getValue(row, name);
        const selected = new Set(
            String(current != null ? current : ALL_MARKETPLACES).split(',').map(s => s.trim()).filter(Boolean)
        );
        if (selected.has(key)) selected.delete(key);
        else selected.add(key);
        setCell(row, name, MARKETPLACES.map(m => m.key).filter(k => selected.has(k)).join(','));
    };

    const changedCount = Object.keys(edits).length;

    const handleSave = async () => {
        setSaving(true);
        setError('');
        // Поля с details:true сохраняются в product_details отдельным PUT (полный upsert,
        // поэтому отправляем все details-значения строки, не только изменённые)
        const detailsNames = editableFields.filter(f => f.details).map(f => f.name);
        const rowsByKey = {};
        rows.forEach(r => { rowsByKey[r[keyField]] = r; });
        const entries = Object.entries(edits);
        const results = await Promise.allSettled(
            entries.map(([id, upd]) => {
                const tableUpd = {};
                let hasTable = false;
                let hasDetails = false;
                for (const [name, value] of Object.entries(upd)) {
                    if (detailsNames.includes(name)) hasDetails = true;
                    else { tableUpd[name] = value; hasTable = true; }
                }
                const reqs = [];
                if (hasTable) reqs.push(api.put(`/api/${tableName}/${encodeURIComponent(id)}`, tableUpd));
                if (hasDetails) {
                    const row = rowsByKey[id] || {};
                    const det = { product_type: tableName };
                    detailsNames.forEach(n => { det[n] = upd[n] !== undefined ? upd[n] : row[n]; });
                    reqs.push(api.put(`/api/product-details/${encodeURIComponent(id)}`, det));
                }
                return Promise.all(reqs);
            })
        );
        setSaving(false);
        const failed = entries.filter((_, i) => results[i].status === 'rejected').map(([id]) => id);
        if (failed.length) {
            setError(`Failed to save: ${failed.join(', ')}`);
            setEdits(prev => Object.fromEntries(Object.entries(prev).filter(([id]) => failed.includes(id))));
            onSaved();
        } else {
            onSaved();
            onClose();
        }
    };

    if (!isOpen) return null;

    const visibleFields = editableFields.filter(f => !hiddenCols[f.name]);
    const keyLabel = (fields || []).find(f => f.name === keyField)?.label || keyField;

    return (
        <div className="bulkedit__overlay">
            <div className="bulkedit">
                <div className="bulkedit__toolbar">
                    <div className="bulkedit__toolbar-left">
                        <h2 className="bulkedit__title">Bulk Edit</h2>
                        <span className="bulkedit__count">
                            {displayedRows.length} rows{changedCount ? `, ${changedCount} edited` : ''}
                        </span>
                    </div>
                    <div className="bulkedit__toolbar-right">
                        {hasBrand && (
                            <select
                                className="bulkedit__brand-filter"
                                value={brandFilter}
                                onChange={(e) => setBrandFilter(e.target.value)}
                            >
                                <option value="">All brands</option>
                                {brandNames.map(b => <option key={b} value={b}>{b}</option>)}
                            </select>
                        )}
                        <details className="bulkedit__columns">
                            <summary>Columns</summary>
                            <div className="bulkedit__columns-list">
                                {editableFields.map(f => (
                                    <label key={f.name}>
                                        <input
                                            type="checkbox"
                                            checked={!hiddenCols[f.name]}
                                            onChange={() => setHiddenCols(prev => ({ ...prev, [f.name]: !prev[f.name] }))}
                                        />
                                        {f.label}
                                    </label>
                                ))}
                            </div>
                        </details>
                        <button className="btn btn__white_g" onClick={onClose} disabled={saving}>Cancel</button>
                        <button className="btn btn__green" onClick={handleSave} disabled={saving || !changedCount}>
                            {saving ? 'Saving...' : `Save${changedCount ? ` (${changedCount})` : ''}`}
                        </button>
                    </div>
                </div>
                {error && <div className="bulkedit__error">{error}</div>}
                <div className="bulkedit__grid">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => handleSort(keyField)}>
                                    {keyLabel}{sort.key === keyField ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                </th>
                                {visibleFields.map(f => (
                                    <th key={f.name} onClick={f.type === 'image' ? undefined : () => handleSort(f.name)}>
                                        {f.label}{sort.key === f.name ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {displayedRows.map((row, rowIndex) => (
                                <tr key={row[keyField]}>
                                    <td className="bulkedit__key-cell">{row[keyField]}</td>
                                    {visibleFields.map(f => {
                                        if (f.type === 'image') {
                                            const sku = row[keyField];
                                            const verKey = `${sku}|${f.kind || ''}`;
                                            return (
                                                <td key={f.name} className="bulkedit__img-cell">
                                                    <ImageWithPreview
                                                        src={`${productImageUrl(sku, f.kind)}?v=${imgVer[verKey] || 0}`}
                                                        alt=""
                                                        style={{ width: 36, height: 36, borderRadius: 4 }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="bulkedit__img-upload"
                                                        title={`Upload ${f.label.toLowerCase()} photo`}
                                                        onClick={() => handleImageUpload(sku, f.kind)}
                                                    >
                                                        <i className="fas fa-upload"></i>
                                                    </button>
                                                </td>
                                            );
                                        }
                                        const val = getValue(row, f.name);
                                        const isFocused = focused && focused.rowKey === row[keyField] && focused.name === f.name;
                                        const selectedMkts = f.type === 'marketplaces'
                                            ? new Set(String(val != null ? val : ALL_MARKETPLACES).split(',').map(s => s.trim()).filter(Boolean))
                                            : null;
                                        return (
                                            <td
                                                key={f.name}
                                                className={`bulkedit__cell${isChanged(row, f.name) ? ' bulkedit__cell--changed' : ''}`}
                                            >
                                                {f.type === 'marketplaces' ? (
                                                    <div
                                                        className="bulkedit__mkts"
                                                        onFocus={() => setFocused({ rowKey: row[keyField], name: f.name })}
                                                    >
                                                        {MARKETPLACES.map(m => (
                                                            <label key={m.key}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedMkts.has(m.key)}
                                                                    onChange={() => toggleMarketplace(row, f.name, m.key)}
                                                                />
                                                                {m.label}
                                                            </label>
                                                        ))}
                                                    </div>
                                                ) : f.type === 'select' ? (
                                                    <select
                                                        value={String(val ?? '')}
                                                        onChange={(e) => setCell(row, f.name, e.target.value)}
                                                        onFocus={() => setFocused({ rowKey: row[keyField], name: f.name })}
                                                    >
                                                        <option value=""></option>
                                                        {(options[f.name] || []).map(o => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={val ?? ''}
                                                        onChange={(e) => setCell(row, f.name, e.target.value)}
                                                        onFocus={() => setFocused({ rowKey: row[keyField], name: f.name })}
                                                    />
                                                )}
                                                {isFocused && rowIndex < displayedRows.length - 1 && (
                                                    <button
                                                        type="button"
                                                        className="bulkedit__filldown"
                                                        title="Fill value down the column"
                                                        onMouseDown={(e) => { e.preventDefault(); fillDown(rowIndex, f.name); }}
                                                    >
                                                        ↓
                                                    </button>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleImageFile} style={{ display: 'none' }} />
            </div>
        </div>
    );
};

export default BulkEditPopup;
